import React, { useState, useRef, useEffect, useCallback, useId } from 'react';

/**
 * Tooltip component
 * Props:
 *   title       - bold heading (optional)
 *   description - main explanation text
 *   example     - italic example text, prefixed with "Ej:"
 *   children    - the trigger element (usually a label or wrapper)
 *   className   - extra classes for the trigger wrapper
 */
export default function Tooltip({ title, description, example, children, className = '' }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'top' });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timerRef = useRef(null);
  const uid = useId();
  const tooltipId = `tooltip-${uid?.replace(/:/g, '')}`;

  const computePosition = useCallback(() => {
    if (!triggerRef?.current || !tooltipRef?.current) return;
    const trigger = triggerRef?.current?.getBoundingClientRect();
    const tooltip = tooltipRef?.current?.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 8;
    const ARROW = 6;

    // Try placements in order: top, bottom, right, left
    const placements = [
      {
        placement: 'top',
        top: trigger?.top - tooltip?.height - GAP - ARROW,
        left: trigger?.left + trigger?.width / 2 - tooltip?.width / 2,
      },
      {
        placement: 'bottom',
        top: trigger?.bottom + GAP + ARROW,
        left: trigger?.left + trigger?.width / 2 - tooltip?.width / 2,
      },
      {
        placement: 'right',
        top: trigger?.top + trigger?.height / 2 - tooltip?.height / 2,
        left: trigger?.right + GAP + ARROW,
      },
      {
        placement: 'left',
        top: trigger?.top + trigger?.height / 2 - tooltip?.height / 2,
        left: trigger?.left - tooltip?.width - GAP - ARROW,
      },
    ];

    const fits = (p) =>
      p?.top >= 4 &&
      p?.top + tooltip?.height <= vh - 4 &&
      p?.left >= 4 &&
      p?.left + tooltip?.width <= vw - 4;

    let chosen = placements?.find(fits) || placements?.[0];

    // Clamp horizontally
    chosen = {
      ...chosen,
      left: Math.max(4, Math.min(chosen?.left, vw - tooltip?.width - 4)),
      top: Math.max(4, Math.min(chosen?.top, vh - tooltip?.height - 4)),
    };

    setPosition(chosen);
  }, []);

  const show = useCallback(() => {
    clearTimeout(timerRef?.current);
    setVisible(true);
  }, []);

  const hide = useCallback(() => {
    timerRef.current = setTimeout(() => setVisible(false), 80);
  }, []);

  useEffect(() => {
    if (visible) {
      // Double rAF: first frame renders the tooltip into the DOM,
      // second frame measures it after layout is complete.
      requestAnimationFrame(() => requestAnimationFrame(() => computePosition()));
    }
  }, [visible, computePosition]);

  useEffect(() => () => clearTimeout(timerRef?.current), []);

  if (!description && !title) return <>{children}</>;

  const arrowStyles = {
    top: {
      bottom: '-5px',
      left: '50%',
      transform: 'translateX(-50%) rotate(45deg)',
      borderRight: '1px solid rgba(255,255,255,0.1)',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    },
    bottom: {
      top: '-5px',
      left: '50%',
      transform: 'translateX(-50%) rotate(45deg)',
      borderLeft: '1px solid rgba(255,255,255,0.1)',
      borderTop: '1px solid rgba(255,255,255,0.1)',
    },
    right: {
      left: '-5px',
      top: '50%',
      transform: 'translateY(-50%) rotate(45deg)',
      borderLeft: '1px solid rgba(255,255,255,0.1)',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    },
    left: {
      right: '-5px',
      top: '50%',
      transform: 'translateY(-50%) rotate(45deg)',
      borderRight: '1px solid rgba(255,255,255,0.1)',
      borderTop: '1px solid rgba(255,255,255,0.1)',
    },
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex items-center gap-1 ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={visible ? tooltipId : undefined}
      >
        {children}
        {/* ⓘ icon */}
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: '1.5px solid currentColor',
            fontSize: '9px',
            fontWeight: '700',
            lineHeight: 1,
            color: 'var(--color-text-secondary, #9ca3af)',
            flexShrink: 0,
            cursor: 'help',
            userSelect: 'none',
            opacity: 0.7,
          }}
        >
          i
        </span>
      </span>
      {/* Portal-style fixed tooltip */}
      {visible && (
        <div
          id={tooltipId}
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: 'fixed',
            top: position?.top,
            left: position?.left,
            zIndex: 9999,
            maxWidth: 280,
            minWidth: 160,
            width: 'max-content',
            boxSizing: 'border-box',
            background: '#1e293b',
            color: '#f1f5f9',
            borderRadius: 8,
            padding: '10px 12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
            animation: 'tooltip-fade-in 150ms ease',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {/* Arrow */}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              width: 8,
              height: 8,
              background: '#1e293b',
              ...arrowStyles?.[position?.placement],
            }}
          />
          {title && (
            <p
              style={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontSize: '0.75rem',
                fontWeight: '700',
                color: '#f8fafc',
                marginBottom: description ? 4 : 0,
                lineHeight: 1.4,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
                margin: 0,
                marginBottom: description ? 4 : 0,
              }}
            >
              {title}
            </p>
          )}
          {description && (
            <p
              style={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontSize: '0.75rem',
                color: '#cbd5e1',
                lineHeight: 1.5,
                margin: 0,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {description}
            </p>
          )}
          {example && (
            <p
              style={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontSize: '0.6875rem',
                color: '#94a3b8',
                fontStyle: 'italic',
                marginTop: 5,
                lineHeight: 1.4,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              Ej: {example}
            </p>
          )}
        </div>
      )}
      <style>{`
        @keyframes tooltip-fade-in {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
}
